

import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Observable, of, throwError } from 'rxjs';
import { map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as AppStoreSelectors from 'apps/middle/src/app/root-store/app-store/selectors';
import { AppConstants } from 'apps/middle/src/app/app.constants';
import { objectToFormData, IListState, getListApiHttpParam, mapToListModel, IFilterOperator, uuid } from '@aston/foundation';

import { ImportHistoryListSortProp, ImportType, CompletedAccountingListSortProp, ImportReportErrorColumnsListSortProp, ImportReportErrorLinesListSortProp } from '../enums';
import { mapToImportItemModel, mapToImportFileColumnMappingApi, mapToImportDocumentTypeMappingApi, mapToImportPaymentTypeMappingApi, mapToImportFileConfigurationApi, mapToReportErrorColumnItemModel, mapToReportErrorLineItemModel, mapToImportHistoryListApi, mapToImportCountryMappingApi, mapToImportRecoveryAdvisorMappingApi, mapToImportInvoicingCurrencyMappingApi, mapToImportCivilityMappingApi, mapToImportConfiguration, mapToImportOperationTypeMappingApi, mapToImportLanguageMappingApi, mapToImportBooleanMappingApi, mapToImportSectorMappingApi } from '../functions';
import { IImportItemApi, IImportErrorColumnRowModelApi, IImportFileReportErrorLinesListApi, IImportHistoryItemListApi, IImportHistoryItemApi } from '../models/api';
import { IImportItem, IImportFileColumnMapping, IImportDocumentTypeMapping, IImportPaymentTypeMapping, IImportFileConfiguration, IImportErrorGroupRowList, IImportErrorColumnRowModel, IImportFileReportErrorLinesList, IImportHistoryItem, IImportHistoryItemList, IImportCountryMapping, IImportRecoveryAdvisorMapping, IImportCurrenciesMapping, ICompletedAccountingItemList, IImportCivilityMapping, IImportConfiguration, IImportTypeWithImportColumn, IImportColumn, IImportOperationTypeMapping, IImportLanguageMapping, IImportBooleanMapping, IImportSectorMapping } from '../models';

import { ImportListFilterService } from './import-list-filter-spec.service';



@Injectable({
	providedIn: 'root'
})
export class ImportService {
	private http = inject(HttpClient);
	private store = inject(Store);
	private translateService = inject(TranslateService);
	private filterService = inject(ImportListFilterService);


	equalOperator = { operator: IFilterOperator.Equals, allowMultiple: false, translateValues: false }
	columnFieldFilterSpec = { ...this.filterService.importColumnFieldFilterSpec, operatorSpecs:[this.equalOperator] }
	errorCodeFieldFilterSpec = { ...this.filterService.importErrorCodeFieldFilterSpec, operatorSpecs:[this.equalOperator] }
	statusFieldFilterSpec = { ...this.filterService.importStatusFieldFilterSpec, operatorSpecs:[this.equalOperator] }

	getImport(id: number): Observable<IImportItem> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<IImportItemApi>(`${config.apiUrl}/api/Imports/${id}`)),
			withLatestFrom(this.translateService.get('Import.Creation.ConfigWorkerMessage')),
			map(([model, errMsg]) => mapToImportItemModel(model, errMsg))
		)
	}

	getLastDraftImport(): Observable<IImportItem> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<IImportItemApi>(`${config.apiUrl}/api/Imports/Draft/last`)),
			withLatestFrom(this.translateService.get('Import.Creation.ConfigWorkerMessage')),
			map(([model, errMsg]) => mapToImportItemModel(model, errMsg))
		)
	}

	getDraftImport(type: ImportType): Observable<IImportItem> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<IImportItemApi>(`${config.apiUrl}/api/Imports/Draft/${type}`)),
			withLatestFrom(this.translateService.get('Import.Creation.ConfigWorkerMessage')),
			map(([model, errMsg]) => mapToImportItemModel(model, errMsg))
		)
	}

	checkSelectedFiles(files: File[]): Observable<File[]> {
		const importFileConfig = AppConstants.FILE_UPLOADER_CONFIGS.IMPORT;
		const extensionsValidation = new RegExp(`\\.(${importFileConfig.acceptExtensions.join('|')})$`, 'i');

		if (!files.every(f => extensionsValidation.test(f.name))) {
			return throwError(this.translateService.instant('Toasts.File.CorruptOrWrongFormat'));
		}

		if (!files.every(f => f.name.length <= AppConstants.IMPORT_MAX_FILENAME_SIZE)) {
			return throwError(this.translateService.instant('Import.Creation.FileUpload.FileNameTooLong'));
		}

		return of(files);
	}

	uploadFiles(type: ImportType, file: File[]): Observable<IImportItem> {
		const form = objectToFormData({file});
							  
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post<IImportItemApi>(`${config.apiUrl}/api/Imports/Draft/Upload/${type}`, form)),
			withLatestFrom(this.translateService.get('Import.Creation.ConfigWorkerMessage')),
			map(([model, errMsg]) => mapToImportItemModel(model, errMsg))
		)
	}

	getImportConfiguration(): Observable<IImportConfiguration> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<IImportTypeWithImportColumn[]>(`${config.apiUrl}/api/Configuration/ImportColumns`)),
			map(importColumns => mapToImportConfiguration({ importColumns })),
		);
	}

	updateDraftImportConfiguration(type: ImportType, importFileConfiguration: IImportFileConfiguration): Observable<IImportItem> {
		const mappingApi = mapToImportFileConfigurationApi(importFileConfiguration);

		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post<IImportItemApi>(`${config.apiUrl}/api/Imports/Draft/DataConfiguration/${type}`, mappingApi)),
			withLatestFrom(this.translateService.get('Import.Creation.ConfigWorkerMessage')),
			map(([model, errMsg]) => mapToImportItemModel(model, errMsg))
		)
	}

	updateColumnsMapping(type: ImportType, mapping: IImportFileColumnMapping[]): Observable<void> {
		const mappingApi = mapping.map(m => mapToImportFileColumnMappingApi(m));

		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post<void>(`${config.apiUrl}/api/Imports/Draft/DataColumns/${type}`, mappingApi))
		);
	}

	updateValuesMappingCashing(
		type: ImportType,
		accountingDocumentTypeMapping: IImportDocumentTypeMapping[],
		paymentMeansMapping: IImportPaymentTypeMapping[],
		operationTypesMapping: IImportOperationTypeMapping[],
	): Observable<void> {
		const importAccountingDocumentTypeMappingModels = accountingDocumentTypeMapping.map(m => mapToImportDocumentTypeMappingApi(m));
		const importPaymentTypeMappingModels = paymentMeansMapping.map(m => mapToImportPaymentTypeMappingApi(m));
		const importOperationTypeMappingModels = operationTypesMapping.map(m => mapToImportOperationTypeMappingApi(m));

		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post<void>(`${config.apiUrl}/api/Imports/Draft/DataMappings/${type}`, {
				importAccountingDocumentTypeMappingModels,
				importPaymentTypeMappingModels,
				importOperationTypeMappingModels,
			})
			));
	}

	updateValuesMappingAccounting(type: ImportType, accountingDocumentTypeMapping: IImportDocumentTypeMapping[], paymentMeansMapping: IImportPaymentTypeMapping[], invoicingCurrencyMapping: IImportCurrenciesMapping[]): Observable<void> {
		const importAccountingDocumentTypeMappingModels = accountingDocumentTypeMapping.map(m => mapToImportDocumentTypeMappingApi(m));
		const importPaymentTypeMappingModels = paymentMeansMapping.map(m => mapToImportPaymentTypeMappingApi(m));
		const importCurrencyMappingModels = invoicingCurrencyMapping.map(m => mapToImportInvoicingCurrencyMappingApi(m));

		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post<void>(`${config.apiUrl}/api/Imports/Draft/DataMappings/${type}`, {
				importAccountingDocumentTypeMappingModels,
				importPaymentTypeMappingModels,
				importCurrencyMappingModels
			})
			));
	}

	updateValuesMappingDebtor(type: ImportType, countriesMapping: IImportCountryMapping[], civilityMapping: IImportCivilityMapping[], recoveryAdvisorMapping: IImportRecoveryAdvisorMapping[], languagesMapping: IImportLanguageMapping[], sectorMapping: IImportSectorMapping[]): Observable<void> {
		const importCountryMappingModels = countriesMapping.map(m => mapToImportCountryMappingApi(m));
		const importRecoveryAdvisorMappingModels = recoveryAdvisorMapping.map(m => mapToImportRecoveryAdvisorMappingApi(m));
		const importCivilityMappingModels = civilityMapping.map(m => mapToImportCivilityMappingApi(m));
		const importLanguageMappingModels = languagesMapping.map(m => mapToImportLanguageMappingApi(m));
		const importSectorMappingModels = sectorMapping.map(m => mapToImportSectorMappingApi(m));

		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post<void>(`${config.apiUrl}/api/Imports/Draft/DataMappings/${type}`, {
				importCivilityMappingModels,
				importCountryMappingModels,
				importRecoveryAdvisorMappingModels,
				importLanguageMappingModels,
				importSectorMappingModels,
			})
			));
	}

	updateValuesMappingContact(type: ImportType, civilityMapping: IImportCivilityMapping[], languageMapping: IImportLanguageMapping[], booleanMapping: IImportBooleanMapping[]): Observable<void> {
		const importCivilityMappingModels = civilityMapping.map(m => mapToImportCivilityMappingApi(m));
		const importLanguageMappingModels = languageMapping.map(m => mapToImportLanguageMappingApi(m));
		const importBooleanMappingModels = booleanMapping.map(m => mapToImportBooleanMappingApi(m));

		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post<void>(`${config.apiUrl}/api/Imports/Draft/DataMappings/${type}`, {
				importCivilityMappingModels,
				importLanguageMappingModels,
				importBooleanMappingModels,
			})
			));
	}

	getReportErrorColumns(
		importId: number,
		params: IListState<ImportReportErrorColumnsListSortProp>,
		columnDefs: IImportColumn[] = [],
	): Observable<IImportErrorGroupRowList> {
		const httpParams = getListApiHttpParam(params);
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<IImportErrorColumnRowModelApi[]>(`${config.apiUrl}/api/Imports/${importId}/report/error/columns`, {params: httpParams}).pipe(
				map(apiModel => ({
					items: apiModel.map(i => mapToReportErrorColumnItemModel(i, columnDefs))
				})),
			))
		);
	}

	abortImport(importId: number): Observable<void> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post<void>(`${config.apiUrl}/api/Imports/${importId}/delete`, {}))
		);
	}

	getReportErrorLinesByColumn(
		importId: number, importColumn: IImportErrorColumnRowModel, params: IListState<ImportReportErrorLinesListSortProp>): Observable<IImportFileReportErrorLinesList> {
		params.filters = [
			{
				id: uuid(),
				field: this.columnFieldFilterSpec.field.value,
				values: [importColumn.column],
				operator: IFilterOperator.Equals,
				spec: this.columnFieldFilterSpec,
			},
			{
				id: uuid(),
				field: this.errorCodeFieldFilterSpec.field.value,
				values: [importColumn.errorCode],
				operator: IFilterOperator.Equals,
				spec: this.errorCodeFieldFilterSpec
			},
			{
				id: uuid(),
				field: this.statusFieldFilterSpec.field.value,
				values: [importColumn.status],
				operator: IFilterOperator.Equals,
				spec: this.statusFieldFilterSpec
			}];

		const httpParams = getListApiHttpParam(params);

		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<IImportFileReportErrorLinesListApi>(
				`${config.apiUrl}/api/Imports/${importId}/report/error/lines`, {params: httpParams}
			).pipe(
				map(apiModel => mapToListModel(apiModel, mapToReportErrorLineItemModel)),
			))
		);
	}

	getImportHistory(params: IListState<ImportHistoryListSortProp>): Observable<IImportHistoryItemList> {
		const httpParams = getListApiHttpParam(params);

		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<IImportHistoryItemListApi>(`${config.apiUrl}/api/Imports/History`, {params: httpParams}).pipe(
				map(apiModel => {
					return mapToListModel<IImportHistoryItemApi, IImportHistoryItem>(apiModel, mapToImportHistoryListApi) as IImportHistoryItemList;
				}),
			))
		);
	}

	completeImport(importId: number, updatePlatform: boolean, setAsActive: boolean): Observable<void> {
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.post<void>(`${config.apiUrl}/api/Imports/${importId}/complete`, { updatePlatform, setAsActive }))
		);
	}

	getCompletedAccountingOfImport(importId: number, params: IListState<CompletedAccountingListSortProp>): Observable<ICompletedAccountingItemList> {
		const httpParams = getListApiHttpParam(params);
		return this.store.select(AppStoreSelectors.selectConfig).pipe(
			switchMap(config => this.http.get<ICompletedAccountingItemList>(`${config.apiUrl}/api/Imports/${importId}/complete`, {params: httpParams})),
			map(apiModel => mapToListModel(apiModel, m => m)),
		);
	}
}
